import api from './api';

export type InviteType = 'employee' | 'farmer';
export type InviteStatus = 'pending' | 'active' | 'revoked' | 'rejected';

export interface Invite {
    id: string;
    phone: string;
    invite_type: InviteType;
    status: InviteStatus;
    created_at: string;
    expires_at: string;
    activated_at?: string;
}

export interface InviteLimits {
    employee_remaining: number;
    employee_limit: number;
    farmer_remaining: number;
    farmer_limit: number;
    farmer_reset_date?: string;
}

export interface CreateInviteRequest {
    phone: string;
    invite_type: InviteType;
}

export interface InviteListResponse {
    invites: Invite[];
    limits: InviteLimits;
}

class InviteService {
    /**
     * Get list of invites and current limits for the authenticated user.
     */
    async getInvites(): Promise<InviteListResponse> {
        const response = await api.get<InviteListResponse>('/invites', {
            withCredentials: true
        });
        return response.data;
    }

    /**
     * Create a new invite.
     */
    async createInvite(data: CreateInviteRequest): Promise<Invite> {
        const response = await api.post<Invite>('/invites', data, {
            withCredentials: true
        });
        return response.data;
    }

    /**
     * Revoke an existing invite. The BFF serves POST /api/invites/{id}/revoke (it also ends the
     * revoked employee's live sessions, #3073); there is no DELETE /api/invites/{id}.
     */
    async revokeInvite(inviteId: string): Promise<void> {
        await api.post(`/invites/${inviteId}/revoke`, undefined, {
            withCredentials: true
        });
    }

    /**
     * Get current user's invite limits.
     */
    async getLimits(): Promise<InviteLimits> {
        const response = await api.get<InviteLimits>('/invites/limits', {
            withCredentials: true
        });
        return response.data;
    }
}

export const inviteService = new InviteService();
